'use client';

import Link from 'next/link';
import {
  Shield,
  CheckCircle,
  ArrowRight,
  MapPin,
  Scale,
  Database,
  Brain,
  Layers,
  Building2,
  Handshake,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

const pillars = [
  {
    icon: Database,
    step: '1',
    title: 'Empfangen',
    desc: 'Wir holen alles, was es zu der Firma öffentlich gibt: amtliche Register, Bonitäts- und Bilanzinformationen, Bewertungsplattformen, Stellenanzeigen, regulatorische Verzeichnisse, Branchenpresse, Website, Auszeichnungen.',
    tag: 'Wir gehen dorthin, wo die echten Informationen liegen. Nicht nur, wohin eine Suchmaschine führt.',
  },
  {
    icon: Brain,
    step: '2',
    title: 'Interpretieren',
    desc: 'Ein Personalwechsel allein ist eine Meldung. Personalwechsel plus neue Investoren-Beteiligung plus auslaufende Compliance-Frist plus Solo-Geschäftsführung. Das ist ein Risikobild. Wir setzen die Punkte zusammen.',
    tag: 'Schätzungen werden gekennzeichnet, Quellen genannt, Widersprüche aufgedeckt. Auf einem Niveau, auf dem Sie sonst einen Senior-Researcher daneben bräuchten.',
  },
  {
    icon: Layers,
    step: '3',
    title: 'Zusammenfassen',
    desc: 'Jedes Briefing folgt einer klaren Struktur: Profil, Schlüsselpersonen, Finanzen, Risiken mit Ampel-Logik, Empfehlungen mit Begründung, Datenlücken, Gesprächseinstiege.',
    tag: 'Kein Fließtext, in dem Sie nach Bedeutung suchen. Kein Datendump zum Sortieren. Eine Vorlage, die sofort einsatzbereit ist.',
  },
];

const compareRows: { other: string; us: string }[] = [
  {
    other: 'Acht Tools, vierzehn Tabs, einundzwanzig PDFs',
    us: 'Eine Eingabe. Ein Ergebnis.',
  },
  {
    other: 'Daten kommen roh. Sie filtern, vergleichen, deuten.',
    us: 'Daten kommen interpretiert. Wir filtern, vergleichen, deuten.',
  },
  {
    other: 'Jeder Datenpunkt steht für sich',
    us: 'Wir erkennen Zusammenhänge zwischen den Datenpunkten',
  },
  {
    other: '90 Minuten Recherche, dann ein Stapel Notizen',
    us: 'In Minuten ein Briefing, das im Erstgespräch sofort trägt',
  },
  {
    other: 'Sie wissen, was im Register steht',
    us: 'Sie wissen, was es für Ihr Gespräch bedeutet',
  },
];

const verticals = [
  {
    href: '/versicherung',
    icon: Shield,
    badge: 'Verfügbar',
    badgeVariant: 'default' as const,
    title: 'Für Versicherungsmakler',
    pain: 'Strukturiertes Risikoprofil und konkrete Deckungs-Empfehlung, bevor das Erstgespräch beginnt.',
    bullets: [
      'Deckungssummen für 25+ Sparten von Sach bis Cyber, jede mit Berechnungsweg und Begründung',
      '16-Punkte-Risikocheck mit Ampel-Logik, inkl. NIS2, KRITIS und §34d-Compliance',
      'Schlüsselpersonen mit Solo-GF-Ampel, Nachfolge-Signalen und Reputation',
      'Bonität, Bilanzauffälligkeiten und Compliance-Auflagen aus öffentlichen Registern',
      'Cross-Selling-Hebel mit Begründung aus dem konkreten Risikoprofil',
      'Gesprächseinstiege, die im Erstgespräch sofort funktionieren',
    ],
    cta: 'Versicherungs-Briefing kennenlernen',
  },
  {
    href: '/m-a',
    icon: Handshake,
    badge: 'Bald verfügbar',
    badgeVariant: 'outline' as const,
    title: 'Für M&A-Berater',
    pain: 'Pre-DD-Intelligenz, strukturiert und entscheidungsreif, bevor Sie den ersten Anruf machen.',
    bullets: [
      '14-teiliges Pre-DD-Dossier, sofort einsatzbereit im Investment-Memo',
      'Target-Profil mit Ownership, Cap-Table-Hinweisen, Beteiligungsvehikeln und Nachfolge-Signalen',
      'Bewertungsrahmen in drei Szenarien mit DACH-Peer-Multiples, ARR, Funding-Verlauf und Burn-Rate',
      'Red Flag Matrix mit Score-Methodik und gewichteten Risiken aus Personal, Compliance, Markt',
      '5 bis 7 testbare Investment-Hypothesen mit Pro- und Contra-Evidenz, plus konkrete DD-Anweisung',
      'Deal-Struktur-Empfehlungen und vorbereitete Management-Fragen für die erste Runde',
    ],
    cta: 'Mehr erfahren und vormerken',
  },
];

export function PlatformHomeClient({ isAuthenticated }: { isAuthenticated: boolean }) {
  const ctaHref = isAuthenticated ? '/dashboard' : '/register';

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-teal-50 dark:bg-card">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground leading-tight md:text-5xl">
              Maßgeschneiderte Firmenbriefings für Ihren Anlass.
            </h1>
            <p className="mx-auto mt-5 text-xl font-semibold leading-snug md:text-2xl">
              <span className="block text-foreground">Andere liefern Daten.</span>
              <span className="block text-gradient">Wir liefern Verständnis.</span>
            </p>
            <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground leading-relaxed md:text-lg">
              Unzählige Register, Berichte, Bewertungen und Pressestimmen. Auf Knopfdruck
              eingesammelt, in Zusammenhängen verstanden und zu einem strukturierten Briefing
              verdichtet. Sie bekommen keine Datenliste. Sie bekommen Klarheit.
            </p>
            <div className="mt-8 flex items-center justify-center">
              <Link href={ctaHref}>
                <Button variant="hero" size="lg" className="min-h-[44px]">
                  {isAuthenticated ? 'Zum Dashboard' : 'Kostenlos testen'}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Compare — Der Unterschied */}
      <section id="unterschied" className="bg-background py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              Warum salesbriefing.de für Firmenrecherche?
            </h2>
            <p className="mt-3 text-base font-medium text-muted-foreground md:text-lg">
              Wo andere aufhören, fangen wir an.
            </p>
            <p className="mt-3 text-muted-foreground">
              Klassische Recherche endet bei Daten. Wir bringen Sie zum Verständnis.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-2">
            <Card className="border-border bg-muted/30">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  <X className="h-4 w-4" /> Die übliche Recherche
                </div>
                <ul className="space-y-3">
                  {compareRows.map((row) => (
                    <li
                      key={row.other}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                      <span>{row.other}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-accent/50 bg-accent/5 card-shadow">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-accent">
                  <CheckCircle className="h-4 w-4" /> salesbriefing.de
                </div>
                <ul className="space-y-3">
                  {compareRows.map((row) => (
                    <li key={row.us} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span>{row.us}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pillars — Wie wir das tun */}
      <section id="prozess" className="bg-teal-50 dark:bg-card py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              Vom Firmennamen zum Briefing in drei Schritten, die so niemand sonst geht.
            </h2>
            <p className="mt-3 text-muted-foreground">
              <span className="block">Empfangen. Interpretieren. Zusammenfassen.</span>
              <span className="block">Was klassische Anbieter Ihnen überlassen, übernehmen wir.</span>
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pillars.map((p) => (
              <Card key={p.title} className="card-shadow border-border">
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                      <p.icon className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Schritt {p.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                  <p className="mt-4 border-l-2 border-accent/40 pl-3 text-sm italic text-foreground/80">
                    {p.tag}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vertical selector */}
      <section id="vertikale" className="bg-background py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              Zwei Berufsbilder. Eine Recherche-Engine.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Egal ob Sie ein Versicherungsgespräch oder eine Transaktion vorbereiten: die
              Recherche ist die gleiche, die Ausspielung ist auf Ihr Berufsbild zugeschnitten.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            {verticals.map((v) => (
              <Card
                key={v.href}
                className="card-shadow hover:card-shadow-hover transition-shadow border-border"
              >
                <CardContent className="flex h-full flex-col p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10 text-accent">
                      <v.icon className="h-6 w-6" />
                    </div>
                    <Badge variant={v.badgeVariant}>{v.badge}</Badge>
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{v.title}</h3>
                  <p className="mt-2 text-sm font-medium text-foreground/80">{v.pain}</p>
                  <ul className="mt-4 flex-1 space-y-2">
                    {v.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link href={v.href} className="mt-6">
                    <Button variant="outline" className="w-full">
                      {v.cta}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-teal-50 dark:bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Shield className="h-4 w-4 text-accent" /> DSGVO-konform
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <MapPin className="h-4 w-4 text-accent" /> Hosting in Deutschland
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Building2 className="h-4 w-4 text-accent" /> Made in Germany
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Scale className="h-4 w-4 text-accent" /> Keine Beratung i.S.d. §34d GewO
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-800 to-teal-900 py-20 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white md:text-5xl tracking-tight leading-tight">
            Sie geben den Firmennamen ein.
            <br />
            Wir liefern den Rest.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/80 leading-relaxed">
            Vertriebs- und Deal-Profis aus der DACH-Region nutzen salesbriefing.de, um in Minuten
            zu wissen, wofür sie sonst Stunden recherchieren würden.
          </p>
          <Link href={ctaHref} className="mt-8 inline-block">
            <Button
              size="lg"
              className="text-base font-semibold shadow-lg min-h-[44px] bg-white text-teal-800 hover:bg-white/90"
            >
              {isAuthenticated ? 'Zum Dashboard' : 'Jetzt kostenlos testen'}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Test ohne Kreditkarte
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> DSGVO-konform
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Monatlich kündbar
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
